import { message } from 'antd'
import {
  generateImageCraiyon,
  generateImageDeepAI,
  editImageDeepAI,
  getTaskStatus
} from '../../../../../services/api/aiVideoGeneration'
import {
  createCreativeGuidelineSessionPayload,
  createPayload
} from '../utils/payloadHelpers'
import { usePresignedUpload } from '../../../hooks/usePresignedUpload'
import {
  FRAME_ROLES,
  createShotFrameUpdate
} from '../../ShotFrames/frameUtils'

export const useImageGeneration = ({
  scenes,
  setScenes,
  formData,
  imageProvider,
  setIsLoading,
  videoId
}) => {
  const { uploadFiles, isUploading } = usePresignedUpload({ videoId })

  const updateShotState = (
    prevScenes,
    sceneId,
    shotId,
    isGenerating,
    progress,
    url,
    error,
    isUploading = false,
    frameRole = FRAME_ROLES.start
  ) =>
    prevScenes.map(scene =>
      scene.id === sceneId
        ? {
            ...scene,
            shots: scene.shots.map(shot =>
              shot.id === shotId
                ? {
                    ...shot,
                    isGenerating,
                    isUploading,
                    progress,
                    ...(url &&
                      createShotFrameUpdate(shot, frameRole, url, {
                        source: 'generation'
                      })),
                    ...(error !== undefined ? { error } : {})
                  }
                : shot
            )
          }
        : scene
    )

  const waitForTaskSuccess = async taskId => {
    if (!taskId) {
      throw new Error('Missing task id')
    }

    const maxAttempts = 60
    const delayMs = 10000

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const statusResponse = await getTaskStatus(taskId)
        const data = statusResponse?.data || null
        const status = data?.status || null

        if (status === 'success') {
          return data?.output || ''
        }

        if (status === 'failed' || status === 'error') {
          const err = new Error(data?.error || 'Task failed')
          err._isTerminal = true
          throw err
        }

        if (
          status === 'pending' &&
          (data?.progress === '100%' || data?.progress === 100)
        ) {
          continue
        }
      } catch (err) {
        if (err?._isTerminal === true || err.message === 'Task failed') {
          throw err
        }
        if (attempt === maxAttempts - 1) {
          throw err
        }
      }

      await new Promise(resolve => setTimeout(resolve, delayMs))
    }

    throw new Error('Task polling timed out')
  }

  const generateAllImages = async (allScenes, isInitialGeneration = false) => {
    setIsLoading(true)
    try {
      const queue = allScenes.flatMap(scene =>
        isInitialGeneration
          ? scene.shots
              .filter(shot => !shot.imageSrc || shot.isGenerating)
              .map(shot => ({ scene, shot }))
          : scene.shots.map(shot => ({ scene, shot }))
      )

      const promises = queue.map(async ({ scene, shot }) => {
        try {
          setScenes(prev =>
            updateShotState(prev, scene.id, shot.id, true, 0, '', null)
          )
          const payload = createPayload(shot, formData, videoId)

          const request =
            imageProvider === 'craiyon'
              ? generateImageCraiyon(payload)
              : generateImageDeepAI(payload)
          const response = await request
          const taskId = response?.data?.task_id

          const outputUrl = await waitForTaskSuccess(taskId)

          setScenes(prev =>
            updateShotState(
              prev,
              scene.id,
              shot.id,
              false,
              100,
              outputUrl,
              null
            )
          )
        } catch (error) {
          setScenes(prev =>
            updateShotState(prev, scene.id, shot.id, false, 0, '', {
              message: error?.message || 'Generation failed'
            })
          )
          console.error(
            `Error generating image for scene ${scene.id}, shot ${shot.id}:`,
            error
          )
        }
      })

      await Promise.allSettled(promises)
    } catch (error) {
      message.error('Error generating images')
    } finally {
      setIsLoading(false)
    }
  }

  const retryFailedImages = async allScenes => {
    setIsLoading(true)
    try {
      const queue = allScenes.flatMap(scene =>
        scene.shots.filter(shot => shot.error).map(shot => ({ scene, shot }))
      )

      if (queue.length === 0) {
        setIsLoading(false)
        return
      }

      const promises = queue.map(async ({ scene, shot }) => {
        try {
          // Reset error state and set generating state
          setScenes(prev =>
            updateShotState(prev, scene.id, shot.id, true, 0, '', undefined)
          )
          const payload = createPayload(shot, formData, videoId)

          const request =
            imageProvider === 'craiyon'
              ? generateImageCraiyon(payload)
              : generateImageDeepAI(payload)
          const response = await request
          const taskId = response?.data?.task_id

          const outputUrl = await waitForTaskSuccess(taskId)

          setScenes(prev =>
            updateShotState(
              prev,
              scene.id,
              shot.id,
              false,
              100,
              outputUrl,
              null
            )
          )
        } catch (error) {
          setScenes(prev =>
            updateShotState(prev, scene.id, shot.id, false, 0, '', {
              message: error?.message || 'Generation failed'
            })
          )
          console.error(
            `Error retrying image for scene ${scene.id}, shot ${shot.id}:`,
            error
          )
        }
      })

      await Promise.allSettled(promises)
    } catch (error) {
      message.error('Error retrying failed images')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRegenerateImage = async (sceneId, shotId) => {
    const scene = scenes.find(s => s.id === sceneId)
    const shot = scene?.shots.find(s => s.id === shotId)

    if (!shot) return

    try {
      setScenes(prev =>
        updateShotState(prev, sceneId, shotId, true, 0, '', null)
      )

      const payload = createPayload(shot, formData, videoId)
      const request =
        imageProvider === 'craiyon'
          ? generateImageCraiyon(payload)
          : generateImageDeepAI(payload)
      const response = await request
      const taskId = response?.data?.task_id
      const outputUrl = await waitForTaskSuccess(taskId)

      setScenes(prev =>
        updateShotState(prev, sceneId, shotId, false, 100, outputUrl, null)
      )
    } catch (error) {
      message.error('Regeneration failed')
      console.error('Regeneration failed', error)
      setScenes(prev =>
        updateShotState(prev, sceneId, shotId, false, 0, '', {
          message: error?.message || 'Generation failed'
        })
      )
    }
  }

  const handleSelectReferenceAndRegenerate = async (
    sceneId,
    shotId,
    referenceImageUrl,
    inferenceSteps,
    guidanceScale
  ) => {
    const scene = scenes.find(s => s.id === sceneId)
    const shot = scene?.shots.find(s => s.id === shotId)

    if (!shot) return

    try {
      setScenes(prev =>
        updateShotState(prev, sceneId, shotId, true, 0, '', null)
      )

      const payload = {
        prompt: shot.initial_image_prompt,
        image_url: referenceImageUrl,
        num_inference_steps: inferenceSteps,
        guidance_scale: guidanceScale,
        outfit_map: formData.outfits_reference || {},
        props_map: formData.props_reference || {},
        video_id: videoId,
        ...createCreativeGuidelineSessionPayload(formData)
      }

      const response = await editImageDeepAI(payload)
      const taskId = response?.data?.task_id
      const outputUrl = await waitForTaskSuccess(taskId)

      setScenes(prev =>
        updateShotState(prev, sceneId, shotId, false, 100, outputUrl, null)
      )
      message.success('Image regenerated with reference successfully')
    } catch (error) {
      message.error('Regeneration with reference failed')
      console.error('Regeneration failed', error)
      setScenes(prev =>
        updateShotState(prev, sceneId, shotId, false, 0, '', {
          message: error?.message || 'Generation failed'
        })
      )
    }
  }

  const handleFileUpload = async (
    sceneId,
    shotId,
    file,
    frameRole = FRAME_ROLES.start
  ) => {
    if (!file) return

    try {
      setScenes(prev =>
        updateShotState(prev, sceneId, shotId, true, 0, '', null)
      )

      const response = await uploadFiles({
        files: [file],
        category: 'image'
        // folder: `video_ai/${videoId}/uploads`
      })

      if (response.success && response.urls?.[0]) {
        const uploadedUrl = response.urls[0]
        setScenes(prev =>
          updateShotState(
            prev,
            sceneId,
            shotId,
            false,
            100,
            uploadedUrl,
            null,
            false,
            frameRole
          )
        )
        message.success('Image uploaded successfully')
      } else {
        throw response.error || new Error('Upload failed')
      }
    } catch (error) {
      message.error('File upload failed')
      console.error('Upload failed', error)
      setScenes(prev =>
        updateShotState(
          prev,
          sceneId,
          shotId,
          false,
          0,
          '',
          {
            message: error?.message || 'Upload failed'
          },
          false
        )
      )
    }
  }

  return {
    generateAllImages,
    handleRegenerateImage,
    handleSelectReferenceAndRegenerate,
    retryFailedImages,
    handleFileUpload,
    isUploading
  }
}
